import React from "react";
import { Box, Button, IconButton, useTheme } from "@mui/material";
import {
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

/**
 * SmartPagination Component
 * -----------------------------------------
 * Compact, reusable pagination with ellipsis ("..."),
 * first/last navigation, and page jumping behavior.
 *
 * Props:
 * - count: total number of pages (required)
 * - page: current page (required)
 * - onChange: (event, value) => void (required)
 * - siblingCount: how many pages to show around current (default = 1)
 */
const SmartPagination = ({ count, page, onChange, siblingCount = 1 }) => {
  const theme = useTheme();
  const primary = "#002045";

  if (!count || count <= 1) return null;

  // Generate list of visible pages
  const createPageRange = () => {
    const total = count;
    const current = page;
    const delta = siblingCount;
    const pages = [];

    // If small number of pages — show all
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);
    const showLeftDots = left > 2;
    const showRightDots = right < total - 1;

    const range = [1];
    if (showLeftDots) range.push("left-ellipsis");
    for (let i = left; i <= right; i++) range.push(i);
    if (showRightDots) range.push("right-ellipsis");
    range.push(total);

    return range;
  };

  const displayPages = createPageRange();

  // Handle ellipsis click (jump)
  const handleEllipsisClick = (side) => {
    const jump = side === "left-ellipsis" ? -3 : 3;
    const newPage = Math.min(Math.max(page + jump, 1), count);
    onChange(null, newPage);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        mt: 4,
      }}
    >
      {/* First */}
      <IconButton
        size="small"
        disabled={page === 1}
        onClick={() => onChange(null, 1)}
        sx={{ color: page === 1 ? "rgba(0,0,0,0.3)" : "#000" }}
      >
        <FirstPageIcon fontSize="small" />
      </IconButton>

      {/* Prev */}
      <IconButton
        size="small"
        disabled={page === 1}
        onClick={() => onChange(null, page - 1)}
        sx={{ color: page === 1 ? "rgba(0,0,0,0.3)" : "#000" }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      {/* Page Numbers */}
      {displayPages.map((p, idx) => {
        if (typeof p === "string" && p.includes("ellipsis")) {
          return (
            <Button
              key={idx}
              onClick={() => handleEllipsisClick(p)}
              size="small"
              sx={{
                minWidth: 36,
                fontWeight: 500,
                color: "#666",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
              }}
            >
              ...
            </Button>
          );
        }

        return (
          <Button
            key={idx}
            onClick={(e) => {
              e.preventDefault();
              onChange(null, p);
            }}
            size="small"
            sx={{
              minWidth: 36,
              fontWeight: 600,
              color: p === page ? "#fff" : "#000",
              backgroundColor: p === page ? primary : "transparent",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: p === page ? primary : "rgba(0,0,0,0.05)",
              },
            }}
          >
            {p}
          </Button>
        );
      })}

      {/* Next */}
      <IconButton
        size="small"
        disabled={page === count}
        onClick={() => onChange(null, page + 1)}
        sx={{ color: page === count ? "rgba(0,0,0,0.3)" : "#000" }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>

      {/* Last */}
      <IconButton
        size="small"
        disabled={page === count}
        onClick={() => onChange(null, count)}
        sx={{ color: page === count ? "rgba(0,0,0,0.3)" : "#000" }}
      >
        <LastPageIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default SmartPagination;
